import React, { useState, useEffect } from "react";
import {
  Button as MuiButton,
  TextField,
  FormControl as MuiFormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Backdrop,
  useMediaQuery,
  Grid,
  Stack,
  styled,
  Menu as MuiMenu,
  IconButton as MuiIconButton,
  ListItemButton,
  menuClasses,
  useTheme,
} from "@mui/material";
import { Add, Feed, Search, UploadFile } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { LangConstant } from "const";
import { useUnitList, useDepartmentList, useRole, useUserList } from "hooks";

const Heading = ({
  form,
  userStates,
  userStatus,
  checkedUsers,
  onChangeSearchInput,
  onChangeStateDropdown,
  onChangeStatusDropdown,
  onClickActiveButton,
  onClickInactiveButton,
  onClickDeleteButton,
  onClickImportUser,
  onClickCreateUser,
  handleChangeUnitDropdown,
  handleChangeDepartmentDropdown,
}) => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);

  const { isServiceAdmin } = useRole();
  const { totalUnitList } = useUnitList();
  const { departmentList, handleToggleShowDepartment } = useDepartmentList();
  const { funcExportExcel } = useUserList();

  const [unitList, setUnitList] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [filterDepartmentList, setFilterDepartmentList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickAddUser = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenuOpts = () => {
    setAnchorEl(null);
  };

  const onExport = async () => {
    const response = await funcExportExcel();
    if (response) {
      const link = document.createElement("a");
      link.href = response;
      link.download = "Danh_sach_nguoi_su_dung.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    if (totalUnitList) {
      setUnitList(
        [
          { unitId: 0, unitName: "Tất cả" },
          { unitId: -1, unitName: "Khác" },
        ].concat(
          totalUnitList.sort((a, b) => {
            return a.unitName.localeCompare(b.unitName, "vi");
          }),
        ),
      );
    }
  }, [totalUnitList]);

  useEffect(() => {
    if (Object.keys(departmentList).includes(selectedUnitId)) {
      setFilterDepartmentList(
        [{ departmentId: 0, departmentName: "Tất cả" }].concat(
          departmentList[selectedUnitId].sort((a, b) => {
            return a.departmentName.localeCompare(b.departmentName, "vi");
          }),
        ),
      );
    } else {
      setFilterDepartmentList([{ departmentId: 0, departmentName: "Tất cả" }]);
    }
  }, [departmentList, selectedUnitId]);

  const onChangeUnit = e => {
    setSelectedUnitId(e.target.value);
    handleChangeUnitDropdown(e);
    handleToggleShowDepartment(e.target.value);
  };

  return (
    <Stack direction={{ xl: "row", lg: "column" }} width="100%" gap={2} mb={3}>
      <Stack flexGrow={1}>
        <Grid container spacing={1}>
          <Grid item md sm={12}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              defaultValue={form.search}
              onChange={onChangeSearchInput}
              label={getLabel(LangConstant.P_ADD_NEW_USER)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item md={2} sm={3} sx={{ display: !isServiceAdmin ? "block" : "none" }}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel>{getLabel("OBJ_USER_DETAIL.joinStatus")}</InputLabel>
              <Select
                size="small"
                value={form.state}
                onChange={onChangeStateDropdown}
                label={getLabel("OBJ_USER_DETAIL.joinStatus")}
              >
                {Object.values(userStates)
                  .sort((prev, next) => prev.order - next.order)
                  .map(userState => (
                    <MenuItem key={userState.state} value={userState.state}>
                      {userState.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={2} sm={3} sx={{ display: !isServiceAdmin ? "block" : "none" }}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel>{getLabel("OBJ_USER_DETAIL.activeStatus")}</InputLabel>
              <Select
                size="small"
                value={form.status}
                onChange={onChangeStatusDropdown}
                label={getLabel("OBJ_USER_DETAIL.activeStatus")}
              >
                {Object.values(userStatus)
                  .sort((prev, next) => prev.order - next.order)
                  .map(userStatus => (
                    <MenuItem key={userStatus.status} value={userStatus.status}>
                      {userStatus.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={2} sm={3}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel>Đơn vị</InputLabel>
              <Select size="small" value={form.unitId} onChange={e => onChangeUnit(e)} label="Đơn vị">
                {unitList.map(data => (
                  <MenuItem key={data.unitId} value={data.unitId}>
                    {data.unitName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={2} sm={3}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink>Phòng ban</InputLabel>
              <Select
                size="small"
                value={form.departmentId}
                onChange={handleChangeDepartmentDropdown}
                label="Phòng ban"
                displayEmpty
              >
                {filterDepartmentList.map(data => (
                  <MenuItem key={data.departmentId} value={data.departmentId}>
                    {data.departmentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent={{ xl: "flex-end", lg: "flex-start" }} spacing={1}>
        <Button
          disabled={checkedUsers.data.length === 0}
          onClick={onClickActiveButton}
          variant="contained"
          color="success"
          fullWidth
          sx={{ display: !isServiceAdmin ? "block" : "none" }}
        >
          {getLabel(LangConstant.L_ACTIVE_ACTION)}
        </Button>
        <Button
          disabled={checkedUsers.data.length === 0}
          onClick={onClickInactiveButton}
          variant="contained"
          color="error"
          fullWidth
          sx={{ display: !isServiceAdmin ? "block" : "none" }}
        >
          {getLabel(LangConstant.L_INACTIVE_ACTION)}
        </Button>
        <Button
          disabled={checkedUsers.data.length === 0}
          onClick={onClickDeleteButton}
          variant="contained"
          color="error"
          fullWidth
        >
          {getLabel(LangConstant.TXT_DELETE)}
        </Button>
        <Button onClick={onExport} variant="contained" fullWidth sx={{ display: !isServiceAdmin ? "none" : "block" }}>
          {getLabel(LangConstant.TXT_EXPORT_USER_LIST)}
        </Button>
        <Button onClick={handleClickAddUser} variant="contained" fullWidth startIcon={<Add />}>
          {getLabel("TXT_ADD_NEW")}
        </Button>
      </Stack>

      <AddUserOptions
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenuOpts}
        onClickCreateUser={onClickCreateUser}
        onClickImportUser={onClickImportUser}
      />
    </Stack>
  );
};

export default Heading;

const AddUserOptions = ({ open, anchorEl, onClose, onClickCreateUser, onClickImportUser, ...otherProps }) => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const addUserOptions = [
    { icon: <Feed />, name: getLabel("TXT_FILL_FORM"), onClick: onClickCreateUser },
    { icon: <UploadFile />, name: getLabel("TXT_UPLOAD_FILE"), onClick: onClickImportUser },
  ];

  return (
    <>
      <Backdrop open={open} sx={{ zIndex: theme => theme.zIndex.modal }} />
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isSmallScreen ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isSmallScreen ? "left" : "right",
        }}
        elevation={0}
        {...otherProps}
      >
        <Stack gap={1}>
          {addUserOptions.map(options => (
            <Stack direction={{ xl: "row", xs: "row-reverse" }} spacing={1} onClick={options.onClick}>
              <ButtonLabel>{options.name}</ButtonLabel>
              <IconButton>{options.icon}</IconButton>
            </Stack>
          ))}
        </Stack>
      </Menu>
    </>
  );
};

const Menu = styled(MuiMenu)({
  [`& .${menuClasses.paper}`]: {
    backgroundColor: "transparent",
  },
});

const ButtonLabel = styled(ListItemButton)(props => ({
  backgroundColor: "white",
  minWidth: 160,
  borderRadius: 4,
  justifyContent: "center",
  fontSize: 15,

  "&:hover": {
    background: props.theme.palette.primary.main,
    "&, & *": { color: "white" },
  },
}));

const Button = styled(MuiButton)({
  width: "fit-content",
  minWidth: "unset",
  maxWidth: 180,
});

const IconButton = styled(MuiIconButton)(props => ({
  backgroundColor: "white",
  color: "black",

  "&:hover": {
    background: props.theme.palette.primary.main,
    "&, & *": { color: "white" },
  },
}));

const FormControl = styled(MuiFormControl)({
  width: "100%",
  maxWidth: 200,
});
